import {
  Component,
  Input,
  ViewChild,
  computed,
  signal,
  inject,
  AfterViewInit,
  OnInit,
  DestroyRef,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
  MatOption,
} from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { RoomLocationService } from '@services/room-location.service';
import { LocatingSpace } from '@interfaces/location/locating-space';
import { RoomLocation } from '@interfaces/location/room-location';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type LocationOption = RoomLocation | LocatingSpace;
type LocationMode = 'general' | 'locatingSpaces';

@Component({
  selector: 'app-location-autocomplete',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatInput,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    TranslateModule,
  ],
  templateUrl: './location-autocomplete.component.html',
  styleUrl: './location-autocomplete.component.scss',
})
export class LocationAutocompleteComponent implements AfterViewInit, OnInit {
  @Input({ required: true }) parentForm!: FormGroup;
  @Input() mode: LocationMode = 'general';
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger!: MatAutocompleteTrigger;
  wasUserInteracted = signal(false);

  private roomLocationService = inject(RoomLocationService);
  generalLocations = this.roomLocationService.generalLocations;
  locatingSpaces = this.roomLocationService.locatingSpaces;
  private destroyRef = inject(DestroyRef);

  searchQuery = signal('');

  availableLocations = computed(() => this.getLocations());

  filteredOptions = computed(() => {
    const sq = this.searchQuery().toLowerCase();
    return this.availableLocations().filter((location) =>
      this.getOptionLabel(location).toLowerCase().includes(sq),
    );
  });

  ngOnInit() {
    const control = this.parentForm.get('location');
    const locs = this.availableLocations();
    if (!control) return;

    if (!control.value && locs.length > 0) {
      control.setValue(locs[0]);
    }

    control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value === '') {
          this.wasUserInteracted.set(true);
        }
      });
  }

  ngAfterViewInit() {
    const inputElement = (this.autocompleteTrigger as any).autocomplete?.origin
      ?.nativeElement as HTMLInputElement;

    if (!inputElement) return;

    inputElement.addEventListener('focus', () => {
      if (!this.autocompleteTrigger.panelOpen) {
        const control = this.parentForm.get('location');
        const locs = this.availableLocations();

        if (
          control &&
          this.isEmptyControlValue(control.value) &&
          locs.length > 0 &&
          !this.wasUserInteracted()
        ) {
          control.setValue(locs[0]);
        }
      }
    });
  }

  searchChange(value: string | LocationOption | null) {
    const query =
      typeof value === 'string' ? value : value ? this.getOptionLabel(value) : '';

    if (query !== '') {
      this.wasUserInteracted.set(true);
    }
    this.searchQuery.set(query);
  }

  get isLocationInvalid() {
    const ctrl = this.parentForm.get('location');
    return ctrl?.invalid && (ctrl.dirty || ctrl.touched);
  }

  resetState() {
    this.searchQuery.set('');
    this.parentForm.get('location')?.reset();
  }

  public onDropdownOpened() {
    const control = this.parentForm.get('location');
    const locs = this.availableLocations();
    if (
      control &&
      this.isEmptyControlValue(control.value) &&
      locs.length > 0 &&
      !this.wasUserInteracted()
    ) {
      control.setValue(locs[0]);
    }
  }

  displayLocation(option: LocationOption | string | null): string {
    if (!option) {
      return '';
    }

    return typeof option === 'string' ? option : this.getOptionLabel(option);
  }

  private getOptionLabel(option: LocationOption): string {
    return 'displayName' in option ? option.displayName : option.room;
  }

  private getLocations(): LocationOption[] {
    return this.mode === 'locatingSpaces'
      ? this.locatingSpaces()
      : this.generalLocations();
  }

  private isEmptyControlValue(value: unknown): boolean {
    return (
      value === null ||
      value === undefined ||
      value === '' ||
      (typeof value === 'string' && value.trim() === '')
    );
  }
}
